import { useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { Switch, Tooltip } from '@mantine/core'

import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_VOICE_AGENT } from 'src/graphql/voiceAgents.graphql'
import {
  formatInternationalPhoneNumber,
  removeCountryCode,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'

import SettingsSectionCard from '../../../components/SettingsSectionCard'

const CallTransferCard = ({ voiceAgent, refetchVoiceAgents }) => {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState({
    transferCall: voiceAgent.transferCall,
    transferPhoneNumber: voiceAgent.transferPhoneNumber || '',
  })

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({
        transferCall: voiceAgent.transferCall,
        transferPhoneNumber: voiceAgent.transferPhoneNumber || '',
      })
    }
  }, [voiceAgent, editing])

  const [updateVoiceAgent, { loading }] = useMutation(UPDATE_VOICE_AGENT, {
    onCompleted: () => {
      toast('Voice agent updated successfully', 'success')
      setEditing(false)
      refetchVoiceAgents()
    },
    onError: (error) => {
      toast(`Failed to update voice agent: ${error.message}`, 'error')
    },
  })

  const validatePhone = (phone: string): string | undefined => {
    if (!values.transferCall) return undefined
    if (!phone) return 'Phone number is required when transfer is enabled'
    const phoneLength = unformatPhoneNumber(removeCountryCode(phone)).length
    if (phoneLength !== 10) {
      return 'Number must be 10 digits'
    }
    return undefined
  }

  const phoneError = validatePhone(values.transferPhoneNumber)

  const handleSave = () => {
    updateVoiceAgent({
      variables: {
        voiceAgentId: voiceAgent.voiceAgentId,
        input: {
          transferCall: values.transferCall,
          transferPhoneNumber: unformatPhoneNumber(values.transferPhoneNumber),
        },
      },
    })
  }

  const handleCancel = () => {
    setValues({
      transferCall: voiceAgent.transferCall,
      transferPhoneNumber: voiceAgent.transferPhoneNumber || '',
    })
    setEditing(false)
  }

  return (
    <SettingsSectionCard
      title={
        <div className="flex flex-row items-center gap-2">
          Call Transfer
          <Tooltip label="Admin only">
            <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
          </Tooltip>
        </div>
      }
      initialOpened={false}
      editable
      editing={editing}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
      saveDisabled={!!phoneError}
      loading={loading}
    >
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          {editing ? (
            <div className="flex flex-col gap-2 pt-1">
              <div className="text-sm font-medium text-doubleNickel-gray-700">
                Transfer Call
              </div>
              <Switch
                checked={values.transferCall}
                onChange={(e) =>
                  setValues({
                    ...values,
                    transferCall: e.currentTarget.checked,
                  })
                }
                description="Transfer the call to a recruiter's phone number when requested."
              />
            </div>
          ) : (
            <StyledReadOnlyInput
              label="Transfer Call"
              value={voiceAgent.transferCall ? 'Enabled' : 'Disabled'}
            />
          )}
        </div>

        <div className="col-span-3">
          {editing ? (
            <PhoneInput
              label="Transfer Phone Number"
              value={values.transferPhoneNumber}
              disabled={!values.transferCall}
              onChange={(value) =>
                setValues({ ...values, transferPhoneNumber: value })
              }
              placeholder="Enter phone number"
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
              error={phoneError}
            />
          ) : (
            <StyledReadOnlyInput
              label="Transfer Phone Number"
              value={
                formatInternationalPhoneNumber(
                  voiceAgent.transferPhoneNumber
                ) || '-'
              }
            />
          )}
        </div>
      </div>
    </SettingsSectionCard>
  )
}

export default CallTransferCard
